import React, { useEffect, useState } from 'react';
import { AddCommasToAmount } from '../../helper/Utils';
import { usePpaSchemeInfoExpand } from './PpaSchemeInfoExpandContext';
import './PpaSchemeInfoCell.css';

const formatActivityAmount = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return AddCommasToAmount(num);
};

/** Avoid "CODE-Name - CODE" when name already embeds the code. */
const formatLabeledValue = (name, code) => {
    const label = String(name || '').trim();
    const codeStr = String(code || '').trim();
    if (!label && !codeStr) return '-';
    if (!codeStr) return label;
    if (!label) return codeStr;
    if (
        label === codeStr ||
        label.startsWith(`${codeStr}-`) ||
        label.startsWith(`${codeStr} -`) ||
        label.endsWith(` - ${codeStr}`) ||
        label.endsWith(`-${codeStr}`)
    ) {
        return label;
    }
    return `${label} - ${codeStr}`;
};

/**
 * Normalize activities from list row — array preferred; flat single-split fallback.
 * See docs/FE_PPA_ACTIVITY_SUBACTIVITY.md
 */
export const getPpaActivityRows = (row = {}) => {
    const list = row?.activities || row?.activity_details || [];
    if (Array.isArray(list) && list.length) {
        return list
            .map((item) => ({
                activityId: item?.activity_id ?? null,
                activityName: item?.activity_name || item?.activity || '',
                subactivityId: item?.subactivity_id ?? null,
                subactivityName:
                    item?.subactivity_name ||
                    item?.subactivity ||
                    item?.sub_activity_name ||
                    '',
                amount:
                    item?.amount ??
                    item?.activity_amount ??
                    item?.sanctioned_amount ??
                    '',
            }))
            .filter((item) => item.activityName || item.subactivityName);
    }

    const activityName = row?.activity_name || row?.activity || '';
    const subactivityName =
        row?.subactivity_name ||
        row?.subactivity ||
        row?.sub_activity_name ||
        '';
    const amount = row?.activity_amount ?? row?.amount ?? '';
    if (!activityName && !subactivityName) return [];
    return [
        {
            activityId: row?.activity_id ?? null,
            activityName,
            subactivityId: row?.subactivity_id ?? null,
            subactivityName,
            amount,
        },
    ];
};

/**
 * Scheme + Sub Head always visible.
 * Activity + Subactivity fully hidden; only a small arrow until expanded.
 * Optional expand-all / collapse-all via PpaSchemeInfoExpandProvider.
 */
const PpaSchemeInfoCell = ({ row }) => {
    const expandCtx = usePpaSchemeInfoExpand();
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!expandCtx?.expandSignal) return;
        setExpanded(expandCtx.expandSignal.expanded);
    }, [expandCtx?.expandSignal?.version]);

    const schemeName = row?.scheme_name || row?.scheme || '';
    const schemeCode = row?.scheme_code || row?.schemeCode || row?.code || '';
    const subHeadName = row?.sub_head_name || row?.subhead_name || '';
    const subHeadCode = row?.sub_head_code || row?.subhead_code || '';
    const activities = getPpaActivityRows(row);

    const grouped = [];
    activities.forEach((item) => {
        const last = grouped[grouped.length - 1];
        const key = item.activityName || '—';
        if (last && last.activityName === key) {
            last.items.push(item);
        } else {
            grouped.push({ activityName: key, items: [item] });
        }
    });

    const hasActivities = grouped.length > 0;

    return (
        <div className="ppa-scheme-info">
            <div className="ppa-scheme-info__header">
                <div className="ppa-scheme-info__main">
                    <div className="ppa-scheme-info__block">
                        <div className="ppa-scheme-info__label">Scheme</div>
                        <div className="ppa-scheme-info__value">
                            {formatLabeledValue(schemeName, schemeCode)}
                        </div>
                    </div>

                    <div className="ppa-scheme-info__block">
                        <div className="ppa-scheme-info__label">Sub Head</div>
                        <div className="ppa-scheme-info__value">
                            {formatLabeledValue(subHeadName, subHeadCode)}
                        </div>
                    </div>
                </div>

                {hasActivities ? (
                    <button
                        type="button"
                        className={`ppa-scheme-info__arrow${
                            expanded ? ' ppa-scheme-info__arrow--open' : ''
                        }`}
                        onClick={() => setExpanded((prev) => !prev)}
                        aria-expanded={expanded}
                        aria-label={
                            expanded
                                ? 'Hide activity details'
                                : 'Show activity details'
                        }
                        title={
                            expanded
                                ? 'Hide activity details'
                                : 'Show activity details'
                        }
                    >
                        <span className="ppa-scheme-info__chevron" aria-hidden />
                    </button>
                ) : null}
            </div>

            {hasActivities && expanded ? (
                <div className="ppa-scheme-info__details">
                    {grouped.map((group, groupIndex) => (
                        <div
                            key={`act-${groupIndex}`}
                            className="ppa-scheme-info__group"
                        >
                            <div className="ppa-scheme-info__group-title">
                                <span className="ppa-scheme-info__label">
                                    Activity
                                </span>
                                <span className="ppa-scheme-info__activity-name">
                                    {group.activityName}
                                </span>
                            </div>
                            <div className="ppa-scheme-info__sublist">
                                {group.items.map((item, itemIndex) => {
                                    const amountLabel = formatActivityAmount(
                                        item.amount
                                    );
                                    return (
                                        <div
                                            key={`sub-${groupIndex}-${itemIndex}`}
                                            className="ppa-scheme-info__subrow"
                                        >
                                            <span className="ppa-scheme-info__subname">
                                                {item.subactivityName || '—'}
                                            </span>
                                            {amountLabel ? (
                                                <span className="ppa-scheme-info__amount">
                                                    {amountLabel}
                                                </span>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default PpaSchemeInfoCell;
